import { $ } from "#root/dashboard/l10n";
import { delayAction } from "#root/lib/delay_action";
import { registerEvent } from "#root/lib/win";
import { circularProgressChartBox } from "./circular_progress";
import { flowChartBox } from "./flow_chart";
import { lineChartBox } from "./line_chart";
import { pieChartBox } from "./pie_chart";
import { progressChartBox } from "./progress_chart";
import { waterfallChartBox } from "./water_fall";
import { ukraineMapChartBox, createViewToggleButton } from "./ukraine_map_chart";
import { downloadChartAsCSV, isChartDownloadable } from "./download_chart";

const {
  icon
} = require("#root/lib/dom");

const {
  numberSpacing
} = require("#root/lib/formatting");

const SHOW_VALUES_IN_LEGEND = false;
const SHOW_PRIMARY_VALUE = false;
const SHOW_LEGEND_FOR_SINGLE_SERIES_CHARTS = true;

function createTrendIndicator({
  trend,
  delta,
  text
}) {
  const trendWrapper = document.createElement("span");
  const trendContainer = document.createElement("span");
  trendContainer.classList.add("trend-container");

  if (trend === "up") {
    trendContainer.classList.add("up");
  } else if (trend === "down") {
    trendContainer.classList.add("down");
  }

  if (["up", "down"].includes(trend)) {
    const arrowIcon = icon(require("@Resources/images/icons/trend_arrow.svg"));
    arrowIcon.classList.add("trend-icon");
    trendContainer.appendChild(arrowIcon);
  }

  const trendDelta = document.createElement("span");
  trendDelta.classList.add("trend-delta");
  trendDelta.textContent = delta;
  trendContainer.appendChild(trendDelta);
  const trendText = document.createElement("span");
  trendText.classList.add("trend-text");
  trendText.textContent = text;
  trendWrapper.appendChild(trendContainer);
  trendWrapper.appendChild(trendText);
  return trendWrapper;
}

export function renderChart(chart, context = {}) {
  const chartCard = document.createElement("div");
  chartCard.classList.add("chart-card");
  const chartTitle = document.createElement("h2");
  chartTitle.classList.add("chart-title");
  chartTitle.textContent = chart.title;
  chartCard.appendChild(chartTitle); // Only show download button for supported chart types

  if (isChartDownloadable(chart.type)) {
    const chartDownloadButtonContainer = document.createElement("div");
    chartDownloadButtonContainer.classList.add("chart-download-button-container");
    chartTitle.appendChild(chartDownloadButtonContainer);
    const chartDownloadButton = document.createElement("button");
    chartDownloadButton.classList.add("chart-download-button");
    chartDownloadButton.title = $("download_chart_data");
    chartDownloadButton.appendChild(icon(require("@Resources/images/icons/download.svg")));
    chartDownloadButtonContainer.appendChild(chartDownloadButton);
    chartDownloadButton.addEventListener("click", () => {
      downloadChartAsCSV(chart, context);
    });
  }

  let chartPrimaryValue;

  if ("primaryValue" in chart && (SHOW_PRIMARY_VALUE || !chart.data || chart.data.sets && chart.data.sets.length <= 1)) {
    chartPrimaryValue = document.createElement("h1");
    chartPrimaryValue.classList.add("chart-primary-value");
    chartPrimaryValue.textContent = chart.primaryValueFormatted || numberSpacing(chart.primaryValue);
    chartCard.appendChild(chartPrimaryValue);

    if (!chart.data) {
      chartPrimaryValue.classList.add("big");
    }

    if (chart.data?.sets?.length > 1) {
      chartPrimaryValue.style.display = "none";
    }
  }

  if (chart.subtitle && chartPrimaryValue) {
    const chartSubtitle = document.createElement("h2");
    chartSubtitle.classList.add("chart-subtitle");

    if (chart.data) {
      chartPrimaryValue.appendChild(chartSubtitle);
    } else {
      chartCard.appendChild(chartSubtitle);
    }

    if (chart.subtitle.type === "text") {
      chartSubtitle.textContent = chart.subtitle.text;
    }

    if (chart.subtitle.type === "insight") {
      chartSubtitle.appendChild(createTrendIndicator(chart.subtitle));
    }
  }

  const chartRepaintFrame = document.createElement("div");
  chartRepaintFrame.classList.add("chart-repaint-frame");
  chartCard.appendChild(chartRepaintFrame);
  let repaintFunction = null;
  let lastKnownWidth = window.innerWidth;
  let isRepaintOrdered = false;

  function repaintRoutine(isAnimatedPaint, isInstant = false, repaintArguments) {
    const isAnimated = isAnimatedPaint !== undefined ? isAnimatedPaint : lastKnownWidth !== window.innerWidth;

    if (isRepaintOrdered) {
      clearTimeout(isRepaintOrdered);
    } else {
      lastKnownWidth = window.innerWidth;
    }

    if (isAnimated) {
      chartRepaintFrame.style.transition = "opacity 0.2s ease-in-out, filter 0.2s ease-in-out";
      chartRepaintFrame.style.opacity = 0.7;
      chartRepaintFrame.style.pointerEvents = "none";
      chartRepaintFrame.style.filter = `blur(${Math.min(5 + (window.innerWidth - lastKnownWidth), 20)}px)`;
    }

    isRepaintOrdered = setTimeout(() => {
      chartRepaintFrame.innerHTML = "";
      repaintFunction(repaintArguments);
      isRepaintOrdered = false;
      lastKnownWidth = window.innerWidth;
      delayAction(() => {
        chartRepaintFrame.style.opacity = 1;
        chartRepaintFrame.style.filter = "none";
        chartRepaintFrame.style.pointerEvents = "";
      });
    }, isInstant ? 0 : 700);
  }

  if (chart.type === "line-chart") {
    let destroy;

    repaintFunction = repaintArguments => {
      if (destroy) destroy();
      const box = lineChartBox(chart.data, chartCard, repaintRoutine, repaintArguments);
      destroy = box.destroy;
      chartRepaintFrame.appendChild(box);
    };
  } else if (chart.type === "progress-chart") {
    // does not need repaint
    chartRepaintFrame.appendChild(progressChartBox(chart.data));
  } else if (chart.type === "circular-progress-chart") {
    let destroy;

    repaintFunction = () => {
      if (destroy) destroy();
      const box = circularProgressChartBox(chart.data, chartCard);
      destroy = box.destroy;
      chartRepaintFrame.appendChild(box);
    };
  } else if (chart.type === "pie-chart") {
    let destroy;

    repaintFunction = () => {
      if (destroy) destroy();
      const box = pieChartBox(chart.data, chartCard);
      destroy = box.destroy;
      chartRepaintFrame.appendChild(box);
    };
  } else if (chart.type === "waterfall-chart") {
    repaintFunction = () => chartRepaintFrame.appendChild(waterfallChartBox(chart.data));
  } else if (chart.type === "flow-chart") {
    repaintFunction = () => chartRepaintFrame.appendChild(flowChartBox(chart.data));
  } else if (chart.type === "ukraine-map") {
    chartCard.dataset.type = "ukraine-map";
    const mapBox = ukraineMapChartBox(chart.data, chartCard);
    chartRepaintFrame.appendChild(mapBox); // Add toggle button to header

    const toggleButton = createViewToggleButton(mapBox, chartCard);
    const downloadContainer = chartCard.querySelector(".chart-download-button-container");

    if (downloadContainer) {
      downloadContainer.insertBefore(toggleButton, downloadContainer.firstChild);
    } else {
      chartTitle.appendChild(toggleButton);
    }
  }

  if (repaintFunction) {
    registerEvent(window, "resize", () => {
      repaintRoutine();
    }, true);
  }

  if (repaintFunction) repaintFunction();

  if (chart.data && chart.data.sets && chart.data.sets.length >= 1 && !["progress-chart"].includes(chart.type) && !(chart.type === "line-chart" && chart.data.sets[0].points.length === 1)) {
    let sets = chart.data.sets;

    if (!Array.isArray(chart.data.sets)) {
      sets = Object.values(chart.data.sets);
    }

    if (sets.length > 1 || SHOW_LEGEND_FOR_SINGLE_SERIES_CHARTS) {
      const chartLegendWrapper = document.createElement("div");
      chartLegendWrapper.classList.add("chart-legend-wrapper");
      chartCard.appendChild(chartLegendWrapper);
      const chartLegend = document.createElement("div");
      chartLegend.classList.add("chart-legend");
      chartLegendWrapper.appendChild(chartLegend);
      chartLegend.dataset.count = sets.length;

      for (let [idx, set] of sets.entries()) {
        if (!("label" in set)) continue;
        const chartLegendItem = document.createElement("div");
        chartLegendItem.classList.add("chart-legend-item");
        chartLegendItem.dataset.idx = idx;
        const chartLegendItemColor = document.createElement("div");
        chartLegendItemColor.classList.add("chart-legend-item-color");
        chartLegendItemColor.style.background = set.color ?? set.lineStyle ?? set.fillStyle;
        chartLegendItem.appendChild(chartLegendItemColor);
        const chartLegendItemLabel = document.createElement("div");
        chartLegendItemLabel.classList.add("chart-legend-item-label");
        chartLegendItemLabel.textContent = set.label;
        chartLegendItem.appendChild(chartLegendItemLabel);
        const chartLegendItemValue = document.createElement("div");
        chartLegendItemValue.classList.add("chart-legend-item-value");
        let value = set.value;
        let valueFormatted = set.valueFormatted;
        let primaryPoint;

        if (["pie-chart", "circular-progress-chart"].includes(chart.type)) {
          value = `${set.value}%`;
        } else if (SHOW_VALUES_IN_LEGEND) {
          primaryPoint = set.points?.[set.points?.length - 1];

          if (primaryPoint) {
            value = primaryPoint.y;
          }
        } // Use valueFormatted if available, otherwise format value


        chartLegendItemValue.textContent = valueFormatted !== undefined ? valueFormatted : value !== undefined ? typeof value === "string" ? value : numberSpacing(value) : "";
        chartLegendItem.appendChild(chartLegendItemValue);

        if (primaryPoint && chart?.subtitle?.type === "insight") {
          const trendIndicator = createTrendIndicator({
            trend: primaryPoint.trend,
            delta: primaryPoint.delta,
            text: primaryPoint.text
          });
          trendIndicator.classList.add("chart-legend-item-trend");
          chartLegendItemValue.appendChild(trendIndicator);
        }

        chartLegend.appendChild(chartLegendItem);
      }
    }
  }

  return chartCard;
}